// Аналитика поведения: единая точка событий фронта.
// Два потока из одной точки: window.dataLayer (GA4 через GTM — сниппет подключат продажи) и наша БД
// (AnalyticsEvents). В БД события едут двумя входами:
//   1) заголовок X-Analytics на ближайшем обычном API-запросе (base64 JSON, ≤ 20 событий) — см. axios.js;
//   2) POST /Analytics/Track при уходе со страницы (fetch keepalive) — добивка остатка очереди.
// Дедупликация на сервере по eventId (повтор запроса после 401, потеря ответа), поэтому повторная
// отправка одних и тех же событий безопасна.
// Спека: backend/documentation/company-contracts-and-internal-analytics-spec.md §5.
import { resolvePageName } from "./analyticsPageNames";

export const EVENTS = Object.freeze({
  PAGE_VIEW: "page_view",
});

const MAX_HEADER_EVENTS = 20;
// http.sys по умолчанию принимает ~16 КБ на строку запроса и все заголовки вместе (JWT ≈ 1 КБ); больше 4 КБ
// в X-Analytics не кладём — иначе 400 получит сам запрос пользователя
const MAX_HEADER_BYTES = 4 * 1024;
const SESSION_KEY = "PHC_sid";

let pending = [];
// события, снятые в заголовок и ещё не подтверждённые ответом: ключ — значение заголовка
const inFlight = new Map();
let currentPageName = null;
let options = { getToken: () => null, baseURL: "" };
let initialized = false;

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // fallback для старых браузеров
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// base64 от UTF-8: в заголовке допустим только ASCII, а в событиях бывает кириллица
function toBase64(text) {
  return btoa(unescape(encodeURIComponent(text)));
}

export function getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = uuid();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (e) {
    // приватный режим / запрещённое хранилище — сессия живёт только в памяти
    if (!options.memorySessionId) options.memorySessionId = uuid();
    return options.memorySessionId;
  }
}

export function track(name, params = {}) {
  const event = {
    eventId: uuid(),
    name,
    timestamp: new Date().toISOString(),
    sessionId: getSessionId(),
    pageName: currentPageName,
    params,
  };
  pending.push(event);

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, event_id: event.eventId, page_name: currentPageName, ...params });
  } catch (e) {
    // GA4 не должен ломать приложение
  }

  return event;
}

// Переход роутера. Не трекаем: экраны вне реестра (админка, /test), смену query на том же пути.
export function pageView(to, from) {
  const pageName = resolvePageName(to && to.path);
  if (!pageName) return null;

  // Первая навигация (загрузка приложения, F5): vue-router передаёт from = START с path '/' и пустым matched.
  // Без этой проверки F5 на Главной совпадал бы по пути и не считался переходом, а previous_page был бы ложным 'home'.
  const isInitial = !from || !from.matched || from.matched.length === 0;
  if (!isInitial && from.path === to.path) return null;

  const previous = isInitial ? null : resolvePageName(from.path);
  currentPageName = pageName;

  return track(EVENTS.PAGE_VIEW, {
    page_name: pageName,
    page_path: to.path,
    previous_page: previous,
  });
}

// До 20 событий из очереди → значение заголовка X-Analytics (или null, если очередь пуста)
export function takeForHeader() {
  if (pending.length === 0) return null;
  // берём до 20 событий, но не больше MAX_HEADER_BYTES в base64; хотя бы одно событие уходит всегда
  let count = Math.min(MAX_HEADER_EVENTS, pending.length);
  let header = toBase64(JSON.stringify(pending.slice(0, count)));
  while (count > 1 && header.length > MAX_HEADER_BYTES) {
    count = Math.max(1, Math.floor(count / 2));
    header = toBase64(JSON.stringify(pending.slice(0, count)));
  }
  const batch = pending.splice(0, count);
  inFlight.set(header, batch);
  return header;
}

// Ответ получен (любой статус, кроме сетевой ошибки/5xx) — события доставлены или отброшены сервером
export function ackHeader(header) {
  if (header) inFlight.delete(header);
}

// Запрос не дошёл (сеть, 5xx) — вернуть события в начало очереди, они уедут со следующим запросом
export function requeueHeader(header) {
  if (!header) return;
  const batch = inFlight.get(header);
  if (!batch) return;
  inFlight.delete(header);
  pending = batch.concat(pending);
}

// Уход со страницы: остаток очереди — одним POST с keepalive (sendBeacon не умеет Authorization)
export function flushOnLeave() {
  if (pending.length === 0) return;
  const token = options.getToken && options.getToken();
  if (!token || typeof fetch !== "function") return;

  const batch = pending.splice(0);
  try {
    fetch(`${options.baseURL}/Analytics/Track`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Session-Id": getSessionId(),
      },
      body: JSON.stringify(batch),
    }).catch(() => {});
  } catch (e) {
    // не блокируем закрытие вкладки
  }
}

export function init({ getToken, baseURL }) {
  options = { ...options, getToken, baseURL: baseURL || "" };
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  window.addEventListener("pagehide", flushOnLeave);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushOnLeave();
  });
}

// для отладки в консоли
export function pendingCount() {
  return pending.length;
}

export default {
  EVENTS,
  init,
  track,
  pageView,
  getSessionId,
  takeForHeader,
  ackHeader,
  requeueHeader,
  flushOnLeave,
  pendingCount,
};
